/**
 * B0 — the one shared report-criteria contract.
 *
 * A single serializable payload describes "what report is this": it drives the
 * URL deep-link (B1), the Excel/PDF export params, and the scheduled render (B3).
 * Everything that stores or replays a report selection speaks THIS shape.
 *
 * Date handling is deliberate: a subscription is RECURRING, so the criteria
 * stores the `datePreset` (relative — "last 30 days") + an optional custom range,
 * NOT resolved fixed dates. Each render/export resolves the preset to a concrete
 * window at run time (a daily "last 30 days" report always covers the trailing
 * 30 days, never a frozen span). Only a `custom` preset carries explicit dates.
 *
 * `schemaVersion` is stamped on every payload so a stored subscription created
 * under an older shape can be detected and migrated/rejected rather than
 * mis-parsed (m1). Bump it whenever the field set changes incompatibly.
 *
 * v2 (reports engine spec §2, ISS-065 / T-201):
 * - explicit scope flags (`groupScope` / `modelScope` / `industryScope`:
 *   'all' | 'selected') so an empty list can never silently mean "everything"
 *   once a filter was chosen. 'selected' with an empty list means zero units.
 *   Models and industries follow the filter menus: everything selected is
 *   'all'; anything less is 'selected', so nothing selected stays an empty
 *   report (as on screen), which can't be subscribed to.
 * - custom dates are stored as the calendar days picked (YYYY-MM-DD).
 * - "Use production dates" (`useFullGroup`) is stored as explicit dates
 *   (`datePreset: 'custom'` + both bounds), keeping `useFullGroup` as a label.
 * - v1 payloads are upgraded on read (a non-empty list → 'selected', an empty
 *   one → 'all', which is what v1 meant).
 */
import type {
  DatePreset,
  TimeGranularity,
  ReportViewMode,
  CompareSlot,
} from '@/types/reports';
import { MODEL_LABELS, INDUSTRY_TYPE_ORDER } from '@/lib/constants';

export const REPORT_CRITERIA_SCHEMA_VERSION = 2;

/** 'all' = no filter; 'selected' = only the listed items (an empty list = zero units). */
export type CriteriaScope = 'all' | 'selected';

/** ISO date strings (YYYY-MM-DD) — serializable, unlike the Date objects in state. */
export interface SerializedDateRange {
  start: string | null;
  end: string | null;
}

/** A compare slot with its Date fields flattened to ISO strings (see CompareSlot). */
export interface SerializedCriteriaCompareSlot
  extends Omit<CompareSlot, 'customDateRange' | 'previousDateSettings'> {
  customDateRange?: SerializedDateRange;
}

/**
 * The canonical, serializable report selection. `reportType` mirrors the Reports
 * view mode. `compareSlots` is populated only for `reportType: 'compare'`.
 */
export interface ReportCriteria {
  schemaVersion: number;
  reportType: ReportViewMode; // 'summary' | 'charts' | 'compare'
  companyIds: string[];
  groupScope: CriteriaScope;
  groupIds: string[];        // empty whenever groupScope is 'all'
  modelScope: CriteriaScope;
  modelCodes: string[];      // empty whenever modelScope is 'all'
  industryScope: CriteriaScope;
  industryTypes: string[];   // empty whenever industryScope is 'all'
  datePreset: DatePreset;
  customDateRange: SerializedDateRange; // meaningful only when datePreset === 'custom'
  granularity: TimeGranularity;
  includeAllDeployments: boolean;
  /** "Use production dates" — a label only; the dates are in customDateRange. */
  useFullGroup: boolean;
  compareSlots: SerializedCriteriaCompareSlot[];
}

/** The loose input a caller assembles from Reports state (Dates allowed for ranges). */
export interface ReportCriteriaInput {
  reportType: ReportViewMode;
  companyIds?: string[];
  groupIds?: string[];
  modelCodes?: string[];
  industryTypes?: string[];
  datePreset: DatePreset;
  customDateRange?: { start: Date | string | null; end: Date | string | null } | null;
  granularity: TimeGranularity;
  includeAllDeployments?: boolean;
  /** "Use production dates" is on. */
  useFullGroup?: boolean;
  /** The selected productions' date span (YYYY-MM-DD), used when useFullGroup is on. */
  groupDateRange?: { start: string | null; end: string | null } | null;
  compareSlots?: CompareSlot[];
}

const ALL_MODEL_CODES = Object.keys(MODEL_LABELS);
const ALL_INDUSTRY_TYPES: readonly string[] = INDUSTRY_TYPE_ORDER;

/**
 * A model/industry menu's scope: everything chosen is 'all' (no filter); a
 * subset is 'selected'; nothing chosen is 'selected' with an empty list, which
 * the Reports screen shows as an empty report and the subscribe rules block.
 */
function menuScope(selected: readonly string[], all: readonly string[]): CriteriaScope {
  const chosen = new Set(selected);
  return all.every((value) => chosen.has(value)) ? 'all' : 'selected';
}

const SCOPES: CriteriaScope[] = ['all', 'selected'];

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * A picked Date as the calendar day the user picked (YYYY-MM-DD, local), so
 * the server never reads the day after or before through a UTC conversion.
 * Strings pass through untouched.
 */
const toIso = (d: Date | string | null | undefined): string | null => {
  if (!d) return null;
  if (typeof d === 'string') return d;
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const serializeRange = (r?: { start: Date | string | null; end: Date | string | null } | null): SerializedDateRange => ({
  start: toIso(r?.start),
  end: toIso(r?.end),
});

const REPORT_TYPES: ReportViewMode[] = ['summary', 'charts', 'compare'];
// Closed value sets for the m1 guard — a stored/URL payload's datePreset and
// granularity must be KNOWN values, not merely strings (an unsupported
// "hourly"/"nope" would otherwise pass and mislead the resolver downstream).
// Keep in sync with the DatePreset / TimeGranularity unions in types/reports.ts.
const DATE_PRESETS: DatePreset[] = [
  'today', 'yesterday', 'last-7-days', 'last-30-days', 'last-90-days',
  'this-month', 'last-month', 'this-quarter', 'last-quarter',
  'q1', 'q2', 'q3', 'q4', 'this-year', 'last-year', 'custom',
];
const GRANULARITIES: TimeGranularity[] = ['daily', 'weekly', 'monthly'];

/** A compare slot is trusted only if it has the fields the renderer requires. */
function isValidCompareSlot(s: unknown): s is SerializedCriteriaCompareSlot {
  if (!s || typeof s !== 'object') return false;
  const slot = s as Record<string, unknown>;
  return typeof slot.id === 'string' && Array.isArray(slot.groupIds);
}

/**
 * Build a canonical, serializable criteria payload from Reports state. Stamps the
 * current schema version. Compare slots are kept ONLY for compare reports (a
 * summary/charts subscription carries none), and their Date ranges are flattened
 * to ISO. Unknown/extra fields on the input are dropped — the output is exactly
 * the ReportCriteria shape.
 */
export function serializeReportCriteria(input: ReportCriteriaInput): ReportCriteria {
  const isCompare = input.reportType === 'compare';
  const groupIds = [...(input.groupIds ?? [])];
  const modelCodes = [...(input.modelCodes ?? [])];
  const industryTypes = [...(input.industryTypes ?? [])];
  const modelScope = menuScope(modelCodes, ALL_MODEL_CODES);
  const industryScope = menuScope(industryTypes, ALL_INDUSTRY_TYPES);

  // "Use production dates": store the resolved dates, never a lookup at send.
  // Unresolvable (a bound missing) keeps the preset; subscribeBlockReason stops it.
  const useFullGroup = input.useFullGroup === true;
  const production = input.groupDateRange;
  const productionResolved = useFullGroup && !!production?.start && !!production?.end;

  return {
    schemaVersion: REPORT_CRITERIA_SCHEMA_VERSION,
    reportType: input.reportType,
    companyIds: [...(input.companyIds ?? [])],
    groupScope: groupIds.length > 0 ? 'selected' : 'all',
    groupIds,
    modelScope,
    modelCodes: modelScope === 'selected' ? modelCodes : [],
    industryScope,
    industryTypes: industryScope === 'selected' ? industryTypes : [],
    datePreset: productionResolved ? 'custom' : input.datePreset,
    customDateRange: productionResolved
      ? { start: production!.start, end: production!.end }
      : serializeRange(input.customDateRange),
    granularity: input.granularity,
    includeAllDeployments: input.includeAllDeployments ?? false,
    useFullGroup,
    compareSlots: isCompare
      ? (input.compareSlots ?? []).map((slot) => {
          const { previousDateSettings: _drop, customDateRange, ...rest } = slot;
          return { ...rest, customDateRange: serializeRange(customDateRange) };
        })
      : [],
  };
}

const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

/**
 * Parse a stored/URL criteria payload back into a typed ReportCriteria, or return
 * `null` when it can't be trusted — the m1 guard. Rejects a payload whose
 * `schemaVersion` is missing or newer than this client understands, or whose
 * `reportType`/`datePreset`/`granularity` are absent/invalid. A caller treats
 * null as "stale/incompatible preset — fall back to defaults", never a throw.
 * A v1 payload (older deep links) is upgraded to v2: a non-empty list becomes
 * 'selected', an empty one 'all' (what v1 meant), and useFullGroup is false.
 * A v2 payload must carry known scope values; 'all' never keeps a list.
 * Anything else (missing or newer version) is rejected.
 */
export function deserializeReportCriteria(raw: unknown): ReportCriteria | null {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as Record<string, unknown>;

  const isV1 = c.schemaVersion === 1;
  if (!isV1 && c.schemaVersion !== REPORT_CRITERIA_SCHEMA_VERSION) return null;
  if (typeof c.reportType !== 'string' || !REPORT_TYPES.includes(c.reportType as ReportViewMode)) return null;
  // Validate against the CLOSED value sets, not just typeof string (Sentry/Codex #747).
  if (typeof c.datePreset !== 'string' || !DATE_PRESETS.includes(c.datePreset as DatePreset)) return null;
  if (typeof c.granularity !== 'string' || !GRANULARITIES.includes(c.granularity as TimeGranularity)) return null;

  const groupIds = asStringArray(c.groupIds);
  const modelCodes = asStringArray(c.modelCodes);
  const industryTypes = asStringArray(c.industryTypes);
  const scopeOf = (value: unknown, list: string[]): CriteriaScope | null => {
    if (isV1) return list.length > 0 ? 'selected' : 'all';
    return typeof value === 'string' && SCOPES.includes(value as CriteriaScope) ? (value as CriteriaScope) : null;
  };
  const groupScope = scopeOf(c.groupScope, groupIds);
  const modelScope = scopeOf(c.modelScope, modelCodes);
  const industryScope = scopeOf(c.industryScope, industryTypes);
  if (!groupScope || !modelScope || !industryScope) return null;

  const range = (c.customDateRange ?? {}) as Record<string, unknown>;
  return {
    schemaVersion: REPORT_CRITERIA_SCHEMA_VERSION,
    reportType: c.reportType as ReportViewMode,
    companyIds: asStringArray(c.companyIds),
    groupScope,
    groupIds: groupScope === 'selected' ? groupIds : [],
    modelScope,
    modelCodes: modelScope === 'selected' ? modelCodes : [],
    industryScope,
    industryTypes: industryScope === 'selected' ? industryTypes : [],
    datePreset: c.datePreset as DatePreset,
    customDateRange: {
      start: typeof range.start === 'string' ? range.start : null,
      end: typeof range.end === 'string' ? range.end : null,
    },
    granularity: c.granularity as TimeGranularity,
    includeAllDeployments: c.includeAllDeployments === true,
    useFullGroup: !isV1 && c.useFullGroup === true,
    // Only compare reports carry slots; ignore any on a non-compare payload, and
    // drop structurally-invalid slots so a renderer never reads a bad slot
    // (Codex #747 — [null] / {id-less} were previously cast through untrusted).
    compareSlots: c.reportType === 'compare' && Array.isArray(c.compareSlots)
      ? (c.compareSlots as unknown[]).filter(isValidCompareSlot)
      : [],
  };
}
